import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Container
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { trackFeatureUsage } from '../../utils/analytics';
import { useCompanyMembership } from '../../contexts/CompanyMembershipContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const steps = [
  'Welcome',
  'Overview',
  'Get Started'
];

export default function OnboardingFlow() {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const { companyId } = useCompanyMembership();

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
    trackFeatureUsage('onboarding_step_complete', { step: activeStep });
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = async () => {
    trackFeatureUsage('onboarding_complete');
    
    // Mark onboarding as complete in company document
    if (companyId) {
      try {
        await updateDoc(doc(db, 'companies', companyId), {
          onboardingCompleted: true,
          onboardingCompletedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error marking onboarding as complete:', error);
        // Continue anyway - not critical
      }
    }
    
    navigate('/dashboard');
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h4" gutterBottom>
              Welcome to Site Sync!
            </Typography>
            <Typography variant="body1" paragraph>
              Your account has been created and you're all set up.
            </Typography>
            <Typography variant="body1" paragraph>
              Let's take a quick tour of what you can do with Site Sync.
            </Typography>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 4 }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
              What is Site Sync?
            </Typography>
            
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
              <Typography variant="body1" paragraph>
                Site Sync is your all-in-one contractor management platform. Here's what you can do:
              </Typography>
              
              <Box sx={{ textAlign: 'left', my: 3 }}>
                <Typography variant="h6" gutterBottom>
                  📋 Estimates & Jobs
                </Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  Create professional estimates, get customer signatures, and convert them to jobs. Track job progress with photos, notes, and status updates.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  👥 Customers
                </Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  Manage your customer database, view their history, and keep track of all interactions.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  📅 Schedule
                </Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  Plan your work with the calendar view. Schedule jobs, appointments, and manage your team's availability.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  📦 Inventory
                </Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  Track your materials and tools. Create pick lists for jobs and check items in and out.
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  💰 Invoicing
                </Typography>
                <Typography variant="body2" paragraph color="text.secondary">
                  Generate professional invoices from completed jobs and track payments.
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h4" gutterBottom>
              You're All Set!
            </Typography>
            <Typography variant="body1" paragraph>
              You're ready to start using Site Sync.
            </Typography>
            <Typography variant="body1" paragraph>
              Here's what you can do next:
            </Typography>
            <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                • Create your first estimate
              </Typography>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                • Add your customers
              </Typography>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                • Set up your schedule
              </Typography>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                • Configure your company settings
              </Typography>
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handleComplete : handleNext}
          >
            {activeStep === steps.length - 1 ? 'Get Started' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
} 